// src/pages/ClaimDossier/ClaimDossierTable.tsx
import React from 'react';
import { Badge, Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import {
  PrizeClaimDossier,
  CLAIM_DOSSIER_STATUS_LABELS,
  CLAIM_DOSSIER_STATUS_BADGE,
  CLAIM_DOSSIER_PAYMENT_STATUS_LABELS,
  CLAIM_DOSSIER_PAYMENT_STATUS_BADGE,
  CLAIM_DOSSIER_REVIEW_DECISION_LABELS,
  CLAIM_DOSSIER_REVIEW_DECISION_BADGE,
  CLAIM_DOSSIER_PAYOUT_TYPE_LABELS,
  CLAIM_DOSSIER_PAYOUT_TYPE_BADGE,
} from './types';
import {
  TaxConfig,
  DEFAULT_TAX_CONFIG,
  formatTaxRate,
  computeWithholdingTax,
  computePrizePayoutCommission,
  isTaxRecomputable,
} from './useTaxConfig';
import { AgentMap } from 'helpers/agentLookup';
import { decodeHtmlEntities } from 'helpers/htmlText';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

// Tap action-code cua cot "XU LY" (cot thao tac) trong bang nay — dung de an het cot khi role hien
// tai khong co quyen voi bat ky action nao trong cot (giong PLAN_ACTION_COLUMN_CODES o IssuancePlan).
const DOSSIER_ACTION_COLUMN_CODES = ['button-approve', 'button-cancel', 'button-view', 'button-appraise', 'button-post-audit', 'button-agent-payout'];

interface Props {
  dossiers: PrizeClaimDossier[];
  loading: boolean;
  approvingId?: number | null;
  // Mục 7.1 — tỷ lệ khấu trừ thuế TNCN động (từ system-service), dùng cho tiêu đề cột.
  taxConfig?: TaxConfig;
  onApprove: (dossier: PrizeClaimDossier) => void;
  onCancel: (dossier: PrizeClaimDossier) => void;
  onView: (dossier: PrizeClaimDossier) => void;
  onAppraise: (dossier: PrizeClaimDossier) => void;
  onPostAudit: (dossier: PrizeClaimDossier) => void;
  // Bổ sung — xem thông tin trả thưởng đại lý (chỉ hồ sơ do đại lý tự lập).
  onViewAgentPayout: (dossier: PrizeClaimDossier) => void;
  page: number;
  pageSize: number;
  // Danh sách đại lý (cache) — fallback hiển thị TÊN đại lý khi BE chưa enrich được agentName.
  agentMap?: AgentMap;
}

// "Tên (Mã)" của đại lý — ưu tiên tên BE trả về, fallback tra danh sách đại lý ở FE, cuối cùng mới "ĐL #Id".
const formatAgentLabel = (id?: number | null, name?: string | null, code?: string | null, agentMap?: AgentMap) => {
  if (!id) return '';
  const agent = agentMap?.[Number(id)];
  const agentName = name || agent?.name;
  const agentCode = code || agent?.code;
  if (agentName) return agentCode ? `${agentName} (${agentCode})` : agentName;
  return st('dl_agent_id_text', { agent_id: id });
};

const formatCurrency = (v: number) => st('value_d_text', { value: Number(v || 0).toLocaleString('vi-VN') });

const ClaimDossierTable: React.FC<Props> = ({
  dossiers,
  loading,
  approvingId,
  taxConfig = DEFAULT_TAX_CONFIG,
  onApprove,
  onCancel,
  onView,
  onAppraise,
  onPostAudit,
  onViewAgentPayout,
  page,
  pageSize,
  agentMap,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(location.pathname, DOSSIER_ACTION_COLUMN_CODES);
  // Bổ sung — cột "Đại lý trả thưởng / % HH / Tiền HH" chỉ hiện khi trang có hồ sơ do đại lý lập; % HH và
  // Tiền HH chỉ tính cho hồ sơ trả thưởng TRỰC TIẾP (payoutType = 1), hồ sơ chéo hiện "—".
  const showAgentCommissionCols = dossiers.some((d) => !!d.agentId);
  const colCount = (canShowActionColumn ? 8 : 7) + (showAgentCommissionCols ? 4 : 0);

  return (
  <DataTableShell
    headerRow={
      <tr>
        <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
        <th>{st('ma_hs_ma_ve_column')}</th>
        <th>{st('khach_hang_cccd_column')}</th>
        <th>{st('giai_tien_thuong_column')}</th>
        <th>{st('thue_tncn_column')}{formatTaxRate(taxConfig)})</th>
        <th>{st('thuc_linh_column')}</th>
        {showAgentCommissionCols && (
          <>
            <th>{st('dai_ly_tra_thuong_column')}</th>
            <th className="text-end">{st('hh_tra_thuong_column')}</th>
            <th className="text-end">{st('tien_hh_column')}</th>
            <th className="text-end">{st('thue_tncn_column_2')}</th>
          </>
        )}
        <th>{st('trang_thai_column')}</th>
        {canShowActionColumn && <th>{st('xu_ly_column')}</th>}
      </tr>
    }
  >
        {loading ? (
          <tr>
            <td colSpan={colCount} className="text-center py-4">
              <span className="spinner-border spinner-border-sm" />
            </td>
          </tr>
        ) : dossiers.length === 0 ? (
          <tr>
            <td colSpan={colCount} className="text-center text-muted py-4">
              {st('chua_co_ho_so_nao_text')}
            </td>
          </tr>
        ) : (
          dossiers.map((d, rowIndex) => (
            <tr key={d.id}>
              <td className="text-center">{getSttNumber(page, pageSize, rowIndex)}</td>
              <td>
                <b>{d.dossierCode}</b>
                <br />
                <span className="text-muted fs-12">{st('ve_text')} {d.symbol}-{d.ticketNumber}</span>
                {/* Mục 8.2, README_UPDATE.md — loại trả thưởng (trực tiếp / chéo) của hồ sơ đại lý tự lập */}
                {d.payoutType ? (
                  <>
                    <br />
                    <Badge color={CLAIM_DOSSIER_PAYOUT_TYPE_BADGE[d.payoutType]} className="fs-11">
                      {d.payoutTypeName || CLAIM_DOSSIER_PAYOUT_TYPE_LABELS[d.payoutType]}
                    </Badge>
                    {d.payoutType === 2 && d.ticketOwnerAgentId ? (
                      <span className="text-muted fs-11 ms-1">
                        {st('ve_cua_dai_ly_text', {
                          value: formatAgentLabel(d.ticketOwnerAgentId, d.ticketOwnerAgentName, d.ticketOwnerAgentCode, agentMap),
                        })}
                      </span>
                    ) : null}
                  </>
                ) : null}
              </td>
              <td>
                {d.claimantName}
                <br />
                <span className="text-muted fs-12">{st('cccd_text')} {d.claimantIdNumber}</span>
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1 mb-1">
                  {d.prizes && d.prizes.length > 0 ? (
                    d.prizes.map((p) => (
                      <Badge key={p.prizeTierCode} className="bg-info-subtle text-info fs-11">
                        {p.prizeTierDisplayName}
                      </Badge>
                    ))
                  ) : (
                    <Badge className="bg-info-subtle text-info fs-11">
                      {d.prizeTierDisplayName || d.prizeTierCode}
                    </Badge>
                  )}
                </div>
                <span className="text-muted fs-12">{st('tong_giai_text')} </span>
                <span className="fw-bold">{formatCurrency(d.prizeAmount)}</span>
              </td>
              {(() => {
                // Mục 8 (README.md §7) — hồ sơ chưa ký duyệt: hiển thị Thuế/Thực lĩnh tính lại theo
                // cấu hình HIỆN TẠI (backend ghi đè đúng số này khi ký duyệt). Hồ sơ đã chốt: giữ số đã lưu.
                const recompute = isTaxRecomputable(d.status);
                const { tax, net } = recompute
                  ? computeWithholdingTax(d.prizeAmount, taxConfig)
                  : { tax: d.withholdingTax, net: d.netAmount };
                return (
                  <>
                    <td>
                      -{formatCurrency(tax)}
                      {recompute && tax !== d.withholdingTax && (
                        <div className="text-muted fs-11">{st('theo_cau_hinh_hien_tai_text')}</div>
                      )}
                    </td>
                    <td className="fw-bold">{formatCurrency(net)}</td>
                  </>
                );
              })()}
              {showAgentCommissionCols && (
                <>
                  <td>{d.agentId ? formatAgentLabel(d.agentId, d.agentName, d.agentCode, agentMap) : <span className="text-muted">—</span>}</td>
                  {d.payoutType === 1 ? (
                    <>
                      <td className="text-end">
                        {(d.prizePayoutCommissionRate ?? taxConfig.prizePayoutCommissionRate)}%
                      </td>
                      <td className="text-end fw-bold">
                        {formatCurrency(
                          d.prizePayoutCommissionAmount ??
                            computePrizePayoutCommission(d.prizeAmount, taxConfig),
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-end text-muted">—</td>
                      <td className="text-end text-muted">—</td>
                    </>
                  )}
                  {/* % thuế TNCN áp dụng cho MỌI hồ sơ (snapshot lúc chốt số; fallback cấu hình hiện tại). */}
                  <td className="text-end">{(d.withholdingTaxRate ?? taxConfig.taxRate)}%</td>
                </>
              )}
              <td>
                <Badge color={CLAIM_DOSSIER_STATUS_BADGE[d.status]}>{CLAIM_DOSSIER_STATUS_LABELS[d.status]}</Badge>
              </td>
              {canShowActionColumn && (
                <td>
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    {/* Bổ sung — hồ sơ do đại lý tự lập: xem đại lý nào trả thưởng, trực tiếp/chéo,
                        nếu chéo thì vé thuộc lô nào và phân cho đại lý nào. */}
                    {d.agentId && PageActionPermission.canDisplay(location.pathname, 'button-agent-payout') && (
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        data-action-code="button-agent-payout"
                        data-action-name="Xem thông tin trả thưởng đại lý"
                        onClick={() => onViewAgentPayout(d)}
                        title={st('thong_tin_tra_thuong_dai_ly_title')}
                      >
                        <i className="ri-store-2-line"></i>
                      </Button>
                    )}
                    {d.status === 'PendingApproval' ? (
                      <>
                        {PageActionPermission.canDisplay(location.pathname, 'button-approve') && (
                          <Button
                            color="success"
                            size="sm"
                            data-action-code="button-approve"
                            data-action-name="Ký duyệt hồ sơ"
                            onClick={() => onApprove(d)}
                            disabled={approvingId === d.id}
                          >
                            {approvingId === d.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                {st('dang_duyet_text')}
                              </>
                            ) : (
                              <>
                                <i className="ri-check-double-line me-1"></i>{st('ky_duyet_text')}
                              </>
                            )}
                          </Button>
                        )}
                        {PageActionPermission.canDisplay(location.pathname, 'button-cancel') && (
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            data-action-code="button-cancel"
                            data-action-name="Hủy hồ sơ"
                            onClick={() => onCancel(d)}
                            title={st('huy_title')}
                            disabled={approvingId === d.id}
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        )}
                      </>
                    ) : d.status === 'AgentPaid' ? (
                      // README.md muc 9 — ho so do dai ly tu tao: Tham dinh (buoc 1) roi Hau kiem (buoc 2,
                      // chi hien khi Tham dinh da Phe duyet) — tuan tu, khong cho nhay buoc.
                      <>
                        {!d.appraisalDecision ? (
                          PageActionPermission.canDisplay(location.pathname, 'button-appraise') && (
                            <Button
                              color="warning"
                              size="sm"
                              data-action-code="button-appraise"
                              data-action-name="Thẩm định hồ sơ"
                              onClick={() => onAppraise(d)}
                            >
                              <i className="ri-shield-check-line me-1"></i>{st('tham_dinh_button')}
                            </Button>
                          )
                        ) : (
                          <Badge
                            color={CLAIM_DOSSIER_REVIEW_DECISION_BADGE[d.appraisalDecision]}
                            className="fs-11"
                            title={decodeHtmlEntities(d.appraisalComment) || undefined}
                          >
                            {st('td_text')} {CLAIM_DOSSIER_REVIEW_DECISION_LABELS[d.appraisalDecision]}
                          </Badge>
                        )}
                        {d.appraisalDecision === 'PheDuyet' &&
                          (!d.postAuditDecision ? (
                            PageActionPermission.canDisplay(location.pathname, 'button-post-audit') && (
                              <Button
                                color="info"
                                size="sm"
                                data-action-code="button-post-audit"
                                data-action-name="Hậu kiểm hồ sơ"
                                onClick={() => onPostAudit(d)}
                              >
                                <i className="ri-search-eye-line me-1"></i>{st('hau_kiem_button')}
                              </Button>
                            )
                          ) : (
                            <Badge
                              color={CLAIM_DOSSIER_REVIEW_DECISION_BADGE[d.postAuditDecision]}
                              className="fs-11"
                              title={decodeHtmlEntities(d.postAuditComment) || undefined}
                            >
                              {st('hk_text')} {CLAIM_DOSSIER_REVIEW_DECISION_LABELS[d.postAuditDecision]}
                            </Badge>
                          ))}
                        {PageActionPermission.canDisplay(location.pathname, 'button-view') && (
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            data-action-code="button-view"
                            data-action-name="Xem chi tiết hồ sơ"
                            onClick={() => onView(d)}
                          >
                            <i className="ri-eye-line"></i>
                          </Button>
                        )}
                      </>
                    ) : (
                      PageActionPermission.canDisplay(location.pathname, 'button-view') && (
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          data-action-code="button-view"
                          data-action-name="Xem chi tiết hồ sơ"
                          onClick={() => onView(d)}
                        >
                          <i className="ri-eye-line me-1"></i>{st('xem_button')}
                        </Button>
                      )
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
  </DataTableShell>
  );
};

export default ClaimDossierTable;
